import { Dispatch, SetStateAction, useState } from 'react';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { Group, Page } from '../../../types';
import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../api/users-api';
import { useNavigate } from 'react-router';
import { createGridOverlays } from '../grid-overlay/no-data-overlay-helpers';
import { pagesConfig } from '../../shared/pages-config';
import DeleteMyGroupsDialog from './delete-my-groups-dialog';
import SharedDataGridClient from '../../shared/shared-data-grid-client';
import SharedToolbar from '../../shared/shared-toolbar-latest';

import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Groups from '@mui/icons-material/Groups';
import Logout from '@mui/icons-material/Logout';
import Refresh from '@mui/icons-material/Refresh';

interface MyGroupsGridProps {
  groups: Page<Group[]> | undefined;
  groupsLoading: boolean;
  groupsFetching: boolean;
  paginationModel: {
    cursor: string | null;
    results: Group[];
    nameSpace: string;
    firstPull: boolean;
    searchTime: number;
    pageSize: number;
  };
  setPaginationModel: Dispatch<
    SetStateAction<{
      cursor: string | null;
      results: Group[];
      nameSpace: string;
      firstPull: boolean;
      searchTime: number;
      pageSize: number;
    }>
  >;
}

export default function MyGroupsGrid({
  groups,
  groupsLoading,
  groupsFetching,
  paginationModel,
  setPaginationModel,
}: MyGroupsGridProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [openDeleteUserFromGroup, setOpenDeleteUserFromGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Group Email',
      editable: false,
      sortable: true,
      filterable: true,
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Group Name',
      editable: false,
      sortable: true,
      filterable: true,
      flex: 1,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      getActions: (params) => {
        return [
          <GridActionsCellItem
            key={params.row.id}
            label="Edit"
            onClick={() => {
              setSelectedGroup(params.row);
              setOpenDeleteUserFromGroup(true);
            }}
            icon={
              <Tooltip arrow title="Leave Group">
                <Logout
                  sx={{
                    transform: 'rotate(180deg)',
                  }}
                />
              </Tooltip>
            }
          />,
        ];
      },
    },
  ];

  function CustomToolbar() {
    return (
      <SharedToolbar noColumn noFilter noQuickFilter>
        <Tooltip arrow title="Refresh">
          <IconButton
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.UserGroups, paginationModel],
              });
              [
                UserKeys.DirectoryGroup,
                UserKeys.GroupSettings,
                UserKeys.GroupLabels,
              ].forEach((key) => {
                void queryClient.invalidateQueries({ queryKey: [key] });
              });
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </SharedToolbar>
    );
  }

  const sortedGroups = groups
    ? [...groups.results.sort((a, b) => a.email.localeCompare(b.email))]
    : [];

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Groups', <Groups />),
        }}
        getRowId={(row) => row.email}
        rows={sortedGroups}
        columns={columns}
        onPaginationModelChange={(newModel) => {
          if (newModel.page !== currentPage) {
            setCurrentPage(newModel.page);
          }
          setPaginationModel({
            ...paginationModel,
            firstPull: newModel.page === 0,
            cursor: groups?.cursor || null,
            pageSize: newModel.pageSize,
          });
        }}
        loading={groupsLoading || groupsFetching}
        disableColumnSelector
        onRowClick={(row: GridRowParams) =>
          navigate(`${pagesConfig.groups.link}/${row.row.email}`)
        }
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
      {openDeleteUserFromGroup && selectedGroup ? (
        <DeleteMyGroupsDialog
          open={openDeleteUserFromGroup}
          handleClose={() => setOpenDeleteUserFromGroup(false)}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          paginationModel={paginationModel}
        />
      ) : null}
    </>
  );
}
